import {useState, useRef, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {Search, Bell, HelpCircle, X, AlertTriangle, CheckCircle2, Info, Clock, Ticket, Car, Zap, Menu, Loader2, Tag} from 'lucide-react';
import {Input} from '@/components/ui/input';
import {api, getApiErrorMessage, notificationEndpoints, searchEndpoints} from '@/lib/api';
import {firstNonUuid, withoutUuid} from '@/lib/display';

const notificationTypeConfig = {
    critical: {icon: Zap, color: 'text-red-500', bg: 'bg-red-50'},
    ticket: {icon: Ticket, color: 'text-orange-500', bg: 'bg-orange-50'},
    incident: {icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50'},
    occurrence: {icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50'},
    vehicle: {icon: Car, color: 'text-blue-500', bg: 'bg-blue-50'},
    resolved: {icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-50'},
    info: {icon: Info, color: 'text-blue-500', bg: 'bg-blue-50'},
    warning: {icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50'},
};

const collectionPayload = (payload, key) => {
    const source = payload?.data ?? payload;
    if (Array.isArray(source)) return source;
    if (Array.isArray(source?.data)) return source.data;
    if (Array.isArray(source?.[key])) return source[key];
    return [];
};

const hideChargersInfo = true;
const chargerInfoPattern = /(^|[\s/_-])(chargers?|charging|evse|carregadores?|posto\s+carregamento)([\s/_-]|$)/i;

const unwrapAttributes = (source = {}) => {
    const attributes = source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? source.attributes
        : {};
    return {...source, ...attributes};
};

const isChargerInfo = (...values) => (
    values.some(value => chargerInfoPattern.test(String(value ?? '')))
);

const normalizeBoolean = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return ['1', 'true', 'read', 'seen'].includes(value.toLowerCase());
    return Boolean(value);
};

const formatNotificationTime = (value) => {
    if (!value) return '';
    if (/^h[áa]\s/i.test(String(value))) return value;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    const divisions = [
        {amount: 60, unit: 'second'},
        {amount: 60, unit: 'minute'},
        {amount: 24, unit: 'hour'},
        {amount: 7, unit: 'day'},
        {amount: 4.34524, unit: 'week'},
        {amount: 12, unit: 'month'},
        {amount: Infinity, unit: 'year'},
    ];

    let duration = Math.round((date.getTime() - Date.now()) / 1000);
    for (const division of divisions) {
        if (Math.abs(duration) < division.amount) {
            return new Intl.RelativeTimeFormat('pt-PT', {numeric: 'auto'}).format(Math.round(duration), division.unit);
        }
        duration /= division.amount;
    }

    return '';
};

const normalizeNotificationType = (value) => {
    const normalized = String(value ?? '').toLowerCase().replace(/[-\s]/g, '_');
    const map = {
        danger: 'critical',
        error: 'critical',
        alert: 'critical',
        success: 'resolved',
        completed: 'resolved',
    };
    return map[normalized] || normalized || 'info';
};

const normalizeNotification = (source = {}, index = 0) => {
    const notification = unwrapAttributes(source);
    const type = normalizeNotificationType(notification.type ?? notification.category ?? notification.level);
    const config = notificationTypeConfig[type] ?? notificationTypeConfig.info;

    return {
        id: notification.id ?? notification.uuid ?? `${type}-${index}`,
        type,
        title: withoutUuid(firstNonUuid(notification.title, notification.subject, notification.name), 'Notificação'),
        message: withoutUuid(firstNonUuid(notification.message, notification.body, notification.description), ''),
        time: formatNotificationTime(notification.time ?? notification.created_at ?? notification.createdAt ?? notification.date),
        read: normalizeBoolean(notification.read ?? notification.is_read ?? notification.read_at ?? notification.seen_at),
        icon: config.icon,
        color: config.color,
        bg: config.bg,
    };
};

const normalizeNotifications = (payload) => (
    collectionPayload(payload, 'notifications')
        .map(normalizeNotification)
        .filter(notification => (
            !hideChargersInfo || !isChargerInfo(notification.type, notification.title, notification.message)
        ))
);

const searchTypeConfig = {
    tag: {icon: Tag, label: 'Tag'},
    vehicle: {icon: Car, label: 'Veiculo'},
    resource: {icon: Car, label: 'Veiculo'},
    occurrence: {icon: AlertTriangle, label: 'Ocorrencia'},
    incident: {icon: AlertTriangle, label: 'Ocorrencia'},
    ticket: {icon: Ticket, label: 'Ticket'},
};

const normalizeSearchResult = (source = {}, index = 0) => {
    const result = unwrapAttributes(source);
    const type = String(result.type ?? result.entity ?? 'result').toLowerCase();
    const config = searchTypeConfig[type] ?? {icon: Search, label: 'Resultado'};

    return {
        id: result.id ?? result.uuid ?? `${type}-${index}`,
        type,
        label: config.label,
        title: withoutUuid(firstNonUuid(result.title, result.name, result.reference), 'Resultado'),
        subtitle: withoutUuid(firstNonUuid(result.subtitle, result.description), ''),
        path: result.path ?? '',
        icon: config.icon,
    };
};

const normalizeSearchResults = (payload) => (
    collectionPayload(payload, 'results')
        .map(normalizeSearchResult)
        .filter(result => (
            !hideChargersInfo || !isChargerInfo(result.type, result.label, result.title, result.subtitle, result.path)
        ))
);

export const Topbar = ({
                           onMenuClick = () => {
                           }
                       }) => {
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [loadingNotifications, setLoadingNotifications] = useState(true);
    const [notificationsError, setNotificationsError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchOpen, setSearchOpen] = useState(false);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [searchError, setSearchError] = useState('');
    const panelRef = useRef(null);
    const searchRef = useRef(null);

    const unreadCount = notifications.filter(n => !n.read).length;

    useEffect(() => {
        let cancelled = false;

        const loadNotifications = async () => {
            setLoadingNotifications(true);
            setNotificationsError('');

            try {
                const response = await api.get(notificationEndpoints.list);
                if (!cancelled) setNotifications(normalizeNotifications(response.data));
            } catch (error) {
                if (!cancelled) {
                    setNotifications([]);
                    setNotificationsError(getApiErrorMessage(error, 'Não foi possível carregar as notificações.'));
                }
            } finally {
                if (!cancelled) setLoadingNotifications(false);
            }
        };

        loadNotifications();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const query = searchQuery.trim();
        let cancelled = false;

        if (query.length < 2) {
            setSearchResults([]);
            setSearchError('');
            setLoadingSearch(false);
            return () => {
                cancelled = true;
            };
        }

        setLoadingSearch(true);
        setSearchError('');

        const timeout = window.setTimeout(async () => {
            try {
                const response = await api.get(searchEndpoints.global, {
                    params: {q: query, limit: 8},
                });

                if (!cancelled) {
                    setSearchResults(normalizeSearchResults(response.data));
                    setSearchOpen(true);
                }
            } catch (error) {
                if (!cancelled) {
                    setSearchResults([]);
                    setSearchError(getApiErrorMessage(error, 'Nao foi possivel pesquisar.'));
                    setSearchOpen(true);
                }
            } finally {
                if (!cancelled) setLoadingSearch(false);
            }
        }, 250);

        return () => {
            cancelled = true;
            window.clearTimeout(timeout);
        };
    }, [searchQuery]);

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setSearchOpen(false);
            }
        };

        if (searchOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [searchOpen]);

    const markAllRead = () => {
        setNotifications(prev => prev.map(n => ({...n, read: true})));
    };

    const markAsRead = (id) => {
        setNotifications(prev => prev.map(n => n.id === id ? {...n, read: true} : n));
    };

    const removeNotification = (id) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
    };

    const openSearchResult = (result) => {
        if (result.path) navigate(result.path);
        setSearchOpen(false);
        setSearchQuery('');
    };

    return (
        <div className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200/50 fixed top-0 left-0 md:left-64 right-0 z-20" data-testid="topbar">
            <div className="h-full px-3 md:px-6 flex items-center justify-between gap-2">
                {/* Mobile hamburger */}
                <button
                    type="button"
                    onClick={onMenuClick}
                    className="md:hidden p-2 hover:bg-slate-100 rounded-md transition-colors flex-shrink-0"
                    data-testid="sidebar-open"
                    aria-label="Abrir menu"
                >
                    <Menu className="text-slate-700" size={22}/>
                </button>
                <div className="flex-1 max-w-md min-w-0" ref={searchRef}>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18}/>
                        <Input
                            data-testid="topbar-search"
                            type="text"
                            placeholder="Pesquisar tags, ocorrências, veículos..."
                            className="pl-10 bg-slate-50 border-slate-200"
                            value={searchQuery}
                            onChange={(event) => {
                                setSearchQuery(event.target.value);
                                setSearchOpen(event.target.value.trim().length >= 2);
                            }}
                            onFocus={() => {
                                if (searchQuery.trim().length >= 2) setSearchOpen(true);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Escape') setSearchOpen(false);
                                if (event.key === 'Enter' && searchResults[0]) openSearchResult(searchResults[0]);
                            }}
                        />
                        {loadingSearch && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" size={16}/>
                        )}
                        {searchOpen && searchQuery.trim().length >= 2 && (
                            <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden z-50">
                                <div className="max-h-80 overflow-y-auto">
                                    {(loadingSearch || searchError || searchResults.length === 0) ? (
                                        <div className="py-8 text-center">
                                            <Search className="mx-auto text-slate-300 mb-2" size={28}/>
                                            <p className="text-sm text-slate-500">
                                                {loadingSearch ? 'A pesquisar...' : searchError || 'Sem resultados'}
                                            </p>
                                        </div>
                                    ) : (
                                        searchResults.map((result) => {
                                            const IconComponent = result.icon;
                                            return (
                                                <button
                                                    key={`${result.type}-${result.id}`}
                                                    type="button"
                                                    className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-slate-50 border-b border-slate-50 last:border-b-0"
                                                    onClick={() => openSearchResult(result)}
                                                >
                                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                                                        <IconComponent className="text-slate-600" size={16}/>
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <p className="text-sm font-medium text-slate-900 truncate">{result.title}</p>
                                                            <span className="text-[10px] uppercase tracking-wide text-slate-400 flex-shrink-0">{result.label}</span>
                                                        </div>
                                                        {result.subtitle && (
                                                            <p className="text-xs text-slate-500 mt-0.5 truncate">{result.subtitle}</p>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <button data-testid="help-button" className="p-2 hover:bg-slate-100 rounded-md transition-colors">
                        <HelpCircle className="text-slate-600" size={20}/>
                    </button>

                    {/* Notifications Bell */}
                    <div className="relative" ref={panelRef}>
                        <button
                            data-testid="notifications-button"
                            className={`p-2 rounded-md transition-colors relative ${isOpen ? 'bg-slate-100' : 'hover:bg-slate-100'}`}
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            <Bell className="text-slate-600" size={20}/>
                            {unreadCount > 0 && (
                                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                            )}
                        </button>

                        {/* Notifications Dropdown */}
                        {isOpen && (
                            <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
                                {/* Header */}
                                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-slate-900 text-sm">Notificações</h3>
                                        {unreadCount > 0 && (
                                            <span className="bg-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                        {unreadCount}
                      </span>
                                        )}
                                    </div>
                                    {unreadCount > 0 && (
                                        <button
                                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                            onClick={markAllRead}
                                        >
                                            Marcar todas como lidas
                                        </button>
                                    )}
                                </div>

                                {/* Notifications List */}
                                <div className="max-h-96 overflow-y-auto">
                                    {(loadingNotifications || notificationsError || notifications.length === 0) ? (
                                        <div className="py-10 text-center">
                                            <Bell className="mx-auto text-slate-300 mb-2" size={32}/>
                                            <p className="text-sm text-slate-500">{loadingNotifications ? 'A carregar notificações...' : notificationsError || 'Sem notificações'}</p>
                                        </div>
                                    ) : (
                                        notifications.map((notification) => {
                                            const IconComponent = notification.icon;
                                            return (
                                                <div
                                                    key={notification.id}
                                                    className={`px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer group ${!notification.read ? 'bg-blue-50/30' : ''}`}
                                                    onClick={() => markAsRead(notification.id)}
                                                >
                                                    <div className="flex gap-3">
                                                        <div className={`w-8 h-8 rounded-full ${notification.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                                                            <IconComponent className={notification.color} size={16}/>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <p className={`text-sm font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-700'}`}>
                                                                    {notification.title}
                                                                </p>
                                                                <button
                                                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-slate-200 rounded"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        removeNotification(notification.id);
                                                                    }}
                                                                >
                                                                    <X size={14} className="text-slate-400"/>
                                                                </button>
                                                            </div>
                                                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{notification.message}</p>
                                                            <p className="text-xs text-slate-400 mt-1">{notification.time}</p>
                                                        </div>
                                                        {!notification.read && (
                                                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Footer */}
                                {notifications.length > 0 && (
                                    <div className="px-4 py-2.5 border-t border-slate-100 text-center">
                                        <button className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                                            Ver todas as notificações
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
